import type { IndoorRoomConfig } from "../domain/indoor/indoorRoom";
/**
 * TypeScript types for the designs table
 */

import type { BuildProgress } from "../domain/build/getProjectBuildGuide";
import type { FinishConfig } from "../domain/finishes/types";
import type { SiteConditions } from "../domain/foundation/siteConditions";
import type { WallOpening } from "../domain/framing/generateFramedWall";
import type { InteriorLayout } from "../domain/interior/types";
import type {
	ClimateProfile,
	LocationMode,
	Market,
} from "../domain/location/types";
import type { SaunaRotationDeg } from "../domain/viewer/saunaRotation";
import type { TimeOfDayValue } from "../domain/viewer/timeOfDay";
import type {
	DeckConfig,
	RoofConfig,
	ThermalInputs,
	TwoRoomConfig,
} from "../engines/types";

export const SAUNA_TYPES = ["fyrkantig", "tunna", "tvarum", "inomhus"] as const;

export type SaunaType = (typeof SAUNA_TYPES)[number];

export function isSaunaType(
	value: string | null | undefined,
): value is SaunaType {
	return (SAUNA_TYPES as readonly string[]).includes(value ?? "");
}

export interface VisibilityConfig {
	foundation: boolean;
	framing: boolean;
	panel: boolean;
	roof: boolean;
	frontWall: boolean;
	interior: boolean;
	exterior: boolean;
	deck: boolean;
}

export interface Design {
	id: string;
	user_id: string;
	name: string;
	type: SaunaType;
	market: Market;
	locationMode: LocationMode;
	postalCode?: string;
	climateProfile?: ClimateProfile;
	width: number;
	length: number;
	partitionSplit?: number;
	entryWall?: TwoRoomConfig["entryWall"];
	glazingStyle?: TwoRoomConfig["glazingStyle"];
	saunaPosition?: TwoRoomConfig["saunaPosition"];
	hasRelaxRoom?: boolean;
	timeOfDay?: TimeOfDayValue;
	/**
	 * Whole-building orientation on the plot, degrees about the vertical axis.
	 * Optional: rows written before the field existed hydrate to 0.
	 */
	rotationDeg?: SaunaRotationDeg;
	/**
	 * The wizard's "2/4/6 personer" size-tier choice, if the design's current
	 * dimensions came from the wizard. Caps the 3D viewer's scale-figure
	 * estimate — see docs/notes/2026-07-29-scale-figure-count-and-leg-collision-fixes.md.
	 * Optional: rows written before the field existed, and designs never
	 * seeded from the wizard, hydrate to null/undefined (no cap).
	 */
	wizardCapacityHint?: number | null;
	visibility: VisibilityConfig;
	openings: WallOpening[];
	deckConfig?: DeckConfig | null;
	roofConfig?: RoofConfig;
	buildProgress?: BuildProgress;
	thermalInputs: ThermalInputs;
	siteConditions?: SiteConditions;
	/**
	 * User-authored interior layout. Absent/null on every row written before
	 * issue 130 — and on any design whose interior the user never touched.
	 */
	interiorLayout?: InteriorLayout | null;
	/**
	 * Karaktärsval (issue 134). Absent on every row written before the column
	 * existed — `normalizeFinishes` resolves that to the defaults, which are
	 * exactly the look those designs were drawn with.
	 */
	finishes?: FinishConfig | null;
	/**
	 * Chosen wall height in metres (issue 155). Absent/null on every row
	 * written before the column existed and on any design whose height the
	 * user never changed — both build at the rule default (2.1 m).
	 */
	wallHeightM?: number | null;
	/**
	 * Inomhus only (issues 174/175): existing walls and the house above. Absent
	 * or null = free-standing, height above not given.
	 */
	indoorRoom?: IndoorRoomConfig | null;
	created_at: string;
	updated_at: string;
}

export type DesignInsert = Pick<
	Design,
	| "name"
	| "type"
	| "market"
	| "locationMode"
	| "postalCode"
	| "climateProfile"
	| "width"
	| "length"
	| "partitionSplit"
	| "entryWall"
	| "glazingStyle"
	| "saunaPosition"
	| "hasRelaxRoom"
	| "timeOfDay"
	| "rotationDeg"
	| "wizardCapacityHint"
	| "visibility"
	| "openings"
	| "deckConfig"
	| "roofConfig"
	| "buildProgress"
	| "thermalInputs"
	| "siteConditions"
	| "interiorLayout"
	| "finishes"
	| "wallHeightM"
	| "indoorRoom"
>;
export type DesignUpdate = Partial<DesignInsert>;
